import { useAppStore, SessionState, assertNever } from './appStore'
import { currentAccountId } from './authStore'
import { UserModel } from '../models/UserModel'

// The only code that assigns `session` — keeps every transition's invariants enforced in one
// place instead of relying on each call site to respect them.
export const userOf = (session: SessionState): UserModel | null =>
    session.tag === 'active' || session.tag === 'accountDisabled' ? session.user : null

// Advances every time a brand-new session actually begins (tokenAppeared's real transition,
// below) — not on a same-session credential refresh. Account identity alone can't tell two
// sessions for the *same* account apart (sign out, then sign back in as yourself before an old
// request settles) — this can, since it only moves at the unauthenticated/accountNotFound ->
// resolving edge, exactly the boundary a stale response must not cross.
let sessionGeneration = 0

/** What a caller captures before starting an async request whose result must not be applied
 * once it's no longer for the current session — see isRequestCurrent. */
export type RequestOrigin = { accountId: string | null; generation: number }

export const captureRequestOrigin = (): RequestOrigin => ({
    accountId: currentAccountId(),
    generation: sessionGeneration,
})

/** True if neither the signed-in account nor the session generation has changed since
 * `origin` was captured. Both are necessary: account identity alone misses a sign-out followed
 * by signing back in as the same account (a new session, same id); generation alone would be
 * enough for that, but doesn't itself say anything about *which* account — spelling out both
 * keeps the check legible at each call site rather than relying on one implying the other. */
export const isRequestCurrent = (origin: RequestOrigin): boolean =>
    currentAccountId() === origin.accountId && sessionGeneration === origin.generation

// The only place `session` is actually written — every mutator below goes through this, so
// `userModel` (kept as physical state for the ~20 files that don't care about lifecycle stage)
// can never drift from it by a mutator forgetting the derivation.
const setSession = (session: SessionState): void => useAppStore.setState({ session, userModel: userOf(session) })

export const sessionActions = {
    // Also accepts 'accountNotFound' as a starting tag: that's where a stranded sign-in
    // lands, and a retry from the same Login page must still be able to resolve again.
    // Also accepts 'initializing': a token can arrive (postMessage/URL handoff) before
    // Firebase's own listener has reported anything, and that's enough to resolve directly
    // without waiting on it (MD-1633).
    tokenAppeared(cameFrom: 'login' | 'restoredSession') {
        const current = useAppStore.getState().session
        switch (current.tag) {
            case 'initializing':
            case 'unauthenticated':
            case 'accountNotFound':
                break
            case 'resolving':
            case 'active':
            case 'accountDisabled':
                return
            default:
                return assertNever(current)
        }
        sessionGeneration++
        useAppStore.setState({ session: { tag: 'resolving', cameFrom } })
    },

    /** Firebase's onIdTokenChanged has now reported at least once for this app load, so
     * 'initializing' no longer applies on its own (MD-1633). Only settles the no-token-arrived
     * case into 'unauthenticated' — a concurrent tokenAppeared() in the same callback (a cached
     * user whose token was set) may already have moved past 'initializing' by the time this
     * runs, and this must not stomp on that. No-op once out of 'initializing' for the same
     * reason. */
    firebaseAuthStateKnown() {
        const current = useAppStore.getState().session
        if (current.tag !== 'initializing') return
        setSession({ tag: 'unauthenticated' })
    },

    /** /me resolved a real account. Only meaningful out of 'resolving' — otherwise a stale
     * response could re-enable an account already found to be disabled. */
    userResolved(user: UserModel) {
        const current = useAppStore.getState().session
        switch (current.tag) {
            case 'resolving':
                break
            case 'initializing':
            case 'unauthenticated':
            case 'accountNotFound':
            case 'active':
            case 'accountDisabled':
                return
            default:
                return assertNever(current)
        }
        setSession({ tag: 'active', user })
    },

    // The caller signs out right after (see useUserInit); resetUserScopedState preserves this
    // tag rather than clearing it, so Login can render it.
    accountNotFound() {
        const current = useAppStore.getState().session
        switch (current.tag) {
            case 'resolving':
                break
            case 'initializing':
            case 'unauthenticated':
            case 'accountNotFound':
            case 'active':
            case 'accountDisabled':
                return
            default:
                return assertNever(current)
        }
        setSession({ tag: 'accountNotFound' })
    },

    /** Any request can discover this, not just /me (see api.ts's reportFailure), including one
     * that 403s before /me has resolved a user — so no user is required here. Idempotent: a
     * repeat call while already 'accountDisabled' carries no new information. `origin` is what
     * the failing request was made for (see captureRequestOrigin): on a shared device, a stale
     * disabled-account response must not apply once a different session is current — whether
     * that's a different account, or the same account signed out and back in — the tag checks
     * below don't catch either on their own, since the new session can be 'active' or 'resolving'
     * too. A same-session credential renewal (a 401 partway through this same request) must not
     * look like either of those, which is exactly what isRequestCurrent is for.
     *
     * Written as an explicit allow-list (resolving/active), not an exclusion list, on purpose: an
     * exclusion list defaults to *transitioning* on a tag nobody has thought about yet, which is
     * the wrong default for a one-way, hard-to-undo move like this one. */
    accountDisabled(origin: RequestOrigin) {
        if (!isRequestCurrent(origin)) return
        const current = useAppStore.getState().session
        switch (current.tag) {
            case 'resolving':
            case 'active':
                break
            case 'initializing':
            case 'unauthenticated':
            case 'accountNotFound':
            case 'accountDisabled':
                return
            default:
                return assertNever(current)
        }
        setSession({ tag: 'accountDisabled', user: userOf(current) })
    },

    /** Replaces every direct `useAppStore.setState({ userModel })` call site — the profile
     * PATCH/POST endpoints (accept-agreement, privacy-policy, usage-instructions, language).
     * `origin` is captured by the caller before starting that request (see
     * captureRequestOrigin): on a shared device, a stale result must not apply once a different
     * session is current — whether a different account signed in, or the same account signed out
     * and back in — the `active`/`accountDisabled` tag check alone doesn't catch either, since
     * the *new* session is one of those tags too. */
    userUpdated(user: UserModel, origin: RequestOrigin) {
        if (!isRequestCurrent(origin)) return
        const current = useAppStore.getState().session
        switch (current.tag) {
            case 'active':
            case 'accountDisabled':
                break
            case 'initializing':
            case 'unauthenticated':
            case 'accountNotFound':
            case 'resolving':
                return
            default:
                return assertNever(current)
        }
        setSession({ ...current, user })
    },
}
